import { useCallback, useEffect, useState } from 'react';

const LOCATION_CACHE_KEY = 'gonkle_location_cache';
const WEATHER_CACHE_KEY = 'gonkle_weather_cache';
const WEATHER_TTL_MS = 15 * 60 * 1000;

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore cache write errors (private mode, quota, etc.)
  }
};

const isCacheFresh = (cache, ttlMs) => {
  if (!cache || !cache.timestamp) return false;
  return Date.now() - cache.timestamp < ttlMs;
};

const isSameArea = (a, b) => {
  if (!a || !b) return false;
  return Math.abs(a.lat - b.lat) < 0.1 && Math.abs(a.lon - b.lon) < 0.1;
};

export const useLocalWeather = (apiKey) => {
  const [location, setLocation] = useState(null);
  const [weather, setWeather] = useState(null);
  const [status, setStatus] = useState('idle');
  const [time, setTime] = useState(() => new Date());

  useEffect(() => {
    const cachedLocation = readCache(LOCATION_CACHE_KEY);
    if (cachedLocation && isCacheFresh(cachedLocation, WEATHER_TTL_MS)) {
      setLocation({ lat: cachedLocation.lat, lon: cachedLocation.lon });
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus('unsupported');
      return;
    }

    setStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lon: position.coords.longitude
        };
        setLocation(coords);
        writeCache(LOCATION_CACHE_KEY, { ...coords, timestamp: Date.now() });
        setStatus('ready');
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setStatus('denied');
        } else {
          setStatus('error');
        }
      },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 600000 }
    );
  }, []);

  useEffect(() => {
    if (!apiKey || !location) return;

    const cachedWeather = readCache(WEATHER_CACHE_KEY);
    if (cachedWeather && isCacheFresh(cachedWeather, WEATHER_TTL_MS) && isSameArea(cachedWeather.location, location)) {
      setWeather(cachedWeather.data);
      return;
    }

    const controller = new AbortController();

    const fetchWeather = async () => {
      try {
        const url = `https://api.openweathermap.org/data/2.5/weather?lat=${location.lat}&lon=${location.lon}&appid=${apiKey}&units=metric&lang=en`;
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) return;
        const data = await response.json();
        const payload = {
          temp: Math.round(data.main?.temp ?? 0),
          description: data.weather?.[0]?.description || '',
          icon: data.weather?.[0]?.icon || ''
        };
        setWeather(payload);
        writeCache(WEATHER_CACHE_KEY, {
          location,
          data: payload,
          timestamp: Date.now()
        });
      } catch (error) {
        // Ignore fetch errors for a lightweight UX
      }
    };

    fetchWeather();

    return () => controller.abort();
  }, [apiKey, location]);

  return { location, weather, status, time, requestLocation };
};
